// The one tooltip of the system (DESIGN.md «Подсказка»): a name of an icon-only control, shown on hover and on keyboard focus.

import { cloneElement, useEffect, useId, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import s from './tooltip.module.css';

type Side = 'right' | 'bottom';
type Handlers<E extends Element = Element> = { 'aria-describedby'?: string; onMouseEnter?: (e: React.MouseEvent<E>) => void; onMouseLeave?: (e: React.MouseEvent<E>) => void; onFocus?: (e: React.FocusEvent<E>) => void; onBlur?: (e: React.FocusEvent<E>) => void };

/**
 * The tooltip as a hook: handlers for the control and the hint itself (drawn in a portal, so a scrolling rail does not clip it).
 * A control that must stay one element (a trigger of a menu) puts the node inside itself.
 */
export function useTooltip<E extends Element = Element>(label: string, side: Side = 'right', own: Handlers<E> = {}): { handlers: Handlers<E>; node: ReactNode } {
  const [at, setAt] = useState<{ x: number; y: number } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const tipId = useId();
  useEffect(() => () => clearTimeout(timer.current), []);
  const show = (el: Element, delay: number) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const r = el.getBoundingClientRect();
      setAt(side === 'right' ? { x: r.right + 8, y: r.top + r.height / 2 } : { x: r.left + r.width / 2, y: r.bottom + 6 });
    }, delay);
  };
  const hide = () => { clearTimeout(timer.current); setAt(null); };
  const handlers: Handlers<E> = {
    // A screen reader reads the hint with the control while it is shown.
    ...(at ? { 'aria-describedby': tipId } : {}),
    // A short delay on hover: a pointer passing over a row of buttons does not flash every name.
    onMouseEnter: (e) => { own.onMouseEnter?.(e); show(e.currentTarget, 250); },
    onMouseLeave: (e) => { own.onMouseLeave?.(e); hide(); },
    onFocus: (e) => { own.onFocus?.(e); if (e.currentTarget.matches(':focus-visible')) show(e.currentTarget, 0); },
    onBlur: (e) => { own.onBlur?.(e); hide(); },
  };
  const node = at && createPortal(<span id={tipId} role="tooltip" className={side === 'right' ? s.right : s.bottom} style={{ left: at.x, top: at.y }}>{label}</span>, document.body);
  return { handlers, node };
}

/** Wraps one control with the tooltip. */
export function Tooltip({ label, side = 'right', children }: { label: string; side?: Side; children: ReactElement<Handlers> }) {
  const { handlers, node } = useTooltip(label, side, children.props);
  return <>{cloneElement(children, handlers)}{node}</>;
}
